import { type Draft, produce } from 'immer';

import {
  EditedEmailState,
  NEW_EMAIL_REDUCER_EVENT,
  NEW_EMAIL_VIEW_MODE,
  NewEmailComposerState,
  NewEmailContextProps,
  NewEmailReducerAction,
} from '@/views/emails/context/NewEmails.types.ts';

const assertComposerState = (
  state: Draft<NewEmailComposerState> | null,
  errorMessage: string,
): Draft<NewEmailComposerState> => {
  if (!state) {
    throw new Error(errorMessage);
  }

  return state;
};

export const newEmailReducer = (
  state: NewEmailContextProps['newEmailState'],
  action: NewEmailReducerAction,
): NewEmailContextProps['newEmailState'] => {
  switch (action.type) {
    case NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_ADD: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu załączników');
        composerState.attachments.push(action.payload);
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_REMOVE: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu załączników');
        composerState.attachments = composerState.attachments.filter(
          it => it.uuid !== action.payload.uuid,
        );
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_SET: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu załączników');
        composerState.attachments = [...action.payload];
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_SET_READY: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu załączników');
        const attachment = composerState.attachments.find(
          it => it.attachmentId === action.payload.attachmentId,
        );

        if (attachment) {
          attachment.isReady = true;
        }
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_UPDATE_ID: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu załączników');
        const attachment = composerState.attachments.find(it => it.uuid === action.payload.uuid);

        if (attachment) {
          attachment.attachmentId = action.payload.attachmentId;
          attachment.externalAttachmentId = action.payload.attachmentId;
          if (action.payload.name !== undefined) {
            attachment.name = action.payload.name;
          }
        }
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.CHANGE_WINDOW_MODE: {
      return produce(state, draft => {
        const composerState = assertComposerState(draft, 'Błąd przy aktualizowaniu trybu okna');
        composerState.form = action.payload.form;
        composerState.isCollapsed = action.payload.isCollapsed;
        composerState.windowMode = action.payload.mode;
      });
    }
    case NEW_EMAIL_REDUCER_EVENT.CLEAR_COMPOSER:
    case NEW_EMAIL_REDUCER_EVENT.SEND_SUCCESS: {
      return null;
    }
    case NEW_EMAIL_REDUCER_EVENT.DRAFT_READY: {
      if (
        !state ||
        state.state !== EditedEmailState.IN_PROGRESS ||
        state.draftRequestId !== action.payload.draftRequestId
      ) {
        return state;
      }

      return {
        ...state,
        emailId: action.payload.emailId,
        state: EditedEmailState.READY,
        threadId: action.payload.threadId,
      };
    }
    case NEW_EMAIL_REDUCER_EVENT.OPEN_EXISTING_DRAFT: {
      return {
        ...action.payload,
        state: EditedEmailState.READY,
        windowMode: NEW_EMAIL_VIEW_MODE.DOCKED,
      };
    }
    case NEW_EMAIL_REDUCER_EVENT.WRITE_EMAIL: {
      const { mode } = action.payload;
      if (!mode) {
        throw new Error('Błąd przy tworzeniu nowej wiadomości');
      }

      return {
        ...action.payload,
        state: EditedEmailState.IN_PROGRESS,
        windowMode: NEW_EMAIL_VIEW_MODE.DOCKED,
      };
    }
  }
};
